import React, { useState, useEffect, useCallback, useRef } from 'react';
import './CompCalculator.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const STORAGE_KEY = 'compCalculatorState';
const HISTORY_KEY = 'compCalculatorHistory';
const PENDING_DI_KEY = 'compCalculatorPendingDI';

// BI/CBM threshold targets: both must be met for the 110% multiplier.
const CBM_TARGET = 3;
const DATA_TARGET = 7;

// --- Helpers ---

const loadJSON = (key, fallback) => {
  try {
    const saved = localStorage.getItem(key);
    if (saved) return JSON.parse(saved);
  } catch (e) { /* ignore */ }
  return fallback;
};

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Fiscal months run 22nd to 21st.
const getFiscalMonthKey = (date = new Date()) => {
  let year = date.getFullYear();
  let month = date.getMonth();
  if (date.getDate() >= 22) {
    month += 1;
    if (month > 11) { month = 0; year += 1; }
  }
  return `${year}-${String(month + 1).padStart(2, '0')}`;
};

const getFiscalLabel = (key) => {
  const [year, mStr] = key.split('-');
  const m = parseInt(mStr);
  const prev = m === 1 ? 12 : m - 1;
  return `${MONTH_NAMES[m - 1]} ${year} (${MONTH_NAMES[prev - 1]} 22 - ${MONTH_NAMES[m - 1]} 21)`;
};

const getFiscalLabelShort = (key) => {
  const [year, mStr] = key.split('-');
  return `${MONTH_NAMES[parseInt(mStr) - 1]} ${year}`;
};

// Bi-weekly pay: anchor Feb 6, 2026. Last paycheck of the month, 2 fiscal months later.
const MS_PER_DAY = 86400000;

const getLastPaycheckOfMonth = (year, month) => {
  const lastDay = new Date(year, month, 0); // last day of month (month is 1-indexed here)
  // Use UTC to avoid DST off-by-one errors
  const anchorUTC = Date.UTC(2026, 1, 6);
  const lastDayUTC = Date.UTC(lastDay.getFullYear(), lastDay.getMonth(), lastDay.getDate());
  const daysDiff = Math.round((lastDayUTC - anchorUTC) / MS_PER_DAY);
  const remainder = ((daysDiff % 14) + 14) % 14;
  const payDate = new Date(lastDay);
  payDate.setDate(payDate.getDate() - remainder);
  return payDate;
};

const getPayoutDate = (fiscalKey) => {
  let [y, m] = fiscalKey.split('-').map(Number);
  m += 2;
  if (m > 12) { m -= 12; y += 1; }
  const paycheck = getLastPaycheckOfMonth(y, m);
  return `${MONTH_NAMES[paycheck.getMonth()]} ${paycheck.getDate()}, ${paycheck.getFullYear()}`;
};

const generateFiscalOptions = (startKey, count = 8) => {
  const opts = [];
  let [y, m] = startKey.split('-').map(Number);
  for (let i = 0; i < count; i++) {
    opts.push(`${y}-${String(m).padStart(2, '0')}`);
    m++; if (m > 12) { m = 1; y++; }
  }
  return opts;
};

const fmt = (n) => '$' + (n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

const migrateDeal = (d) => ({
  ...d,
  dealType: d.dealType || 'new_business',
  name: d.name || '',
  expectedConnectMonth: d.expectedConnectMonth || '',
  startingRevenue: d.startingRevenue || '',
  endingRevenue: d.endingRevenue || '',
  icomp: d.icomp || false,
});

// --- Component ---

const CompCalculator = ({ onBack, user }) => {
  // Scope cached data per user so one user's deals/pending DI never leak into
  // another's calculator on a shared browser.
  const uid = user?.id ?? 'anon';
  const storageKey = `${STORAGE_KEY}_${uid}`;
  const historyKey = `${HISTORY_KEY}_${uid}`;
  const pendingKey = `${PENDING_DI_KEY}_${uid}`;
  const saved = loadJSON(storageKey, null);
  const [monthlyQuota, setMonthlyQuota] = useState(saved?.monthlyQuota ?? '');
  const [monthlyTIC, setMonthlyTIC] = useState(saved?.monthlyTIC ?? '4166.67');
  const [cbmLinesSold, setCbmLinesSold] = useState(saved?.cbmLinesSold ?? '');
  const [dataSalesSold, setDataSalesSold] = useState(saved?.dataSalesSold ?? '');
  const [deals, setDeals] = useState(() => (saved?.deals ?? []).map(migrateDeal));
  const [nextDealId, setNextDealId] = useState(saved?.nextDealId ?? 1);
  const [monthLabel, setMonthLabel] = useState(saved?.monthLabel ?? getFiscalMonthKey());
  const [history, setHistory] = useState(() => loadJSON(historyKey, []));
  const [pendingDI, setPendingDI] = useState(() => loadJSON(pendingKey, []));
  const [editingDealId, setEditingDealId] = useState(null);
  const [viewingMonth, setViewingMonth] = useState(monthLabel);
  const [syncing, setSyncing] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const saveTimerRef = useRef(null);

  // --- Backend sync ---
  const syncToBackend = useCallback((stateObj, historyArr, pendingArr) => {
    if (!user?.userId) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      fetch(`${API_URL}/api/comp-data/`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-User-ID': String(user.userId) },
        body: JSON.stringify({ state: stateObj, history: historyArr, pending_di: pendingArr }),
      }).catch(() => {}); // silent fail - localStorage is backup
    }, 2000);
  }, [user?.userId]);

  // Load from backend on mount — rollover waits for this to finish
  useEffect(() => {
    // Remove legacy un-scoped cache keys that leaked between users.
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(HISTORY_KEY);
    localStorage.removeItem(PENDING_DI_KEY);
    if (!user?.userId) {
      setLoaded(true);
      return;
    }
    setSyncing(true);
    fetch(`${API_URL}/api/comp-data/`, {
      headers: { 'X-User-ID': String(user.userId) },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.updated_at) return;
        if (data.state && Object.keys(data.state).length > 0) {
          const s = data.state;
          setMonthlyQuota(s.monthlyQuota ?? '');
          setMonthlyTIC(s.monthlyTIC ?? '4166.67');
          setCbmLinesSold(s.cbmLinesSold ?? '');
          setDataSalesSold(s.dataSalesSold ?? '');
          setDeals((s.deals ?? []).map(migrateDeal));
          setNextDealId(s.nextDealId ?? 1);
          setMonthLabel(s.monthLabel ?? getFiscalMonthKey());
        }
        if (data.history?.length > 0) setHistory(data.history);
        if (data.pending_di?.length > 0) setPendingDI(data.pending_di);
      })
      .catch(() => {})
      .finally(() => {
        setSyncing(false);
        setLoaded(true);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save to localStorage + queue backend sync (only after initial load)
  useEffect(() => {
    if (!loaded) return;
    const stateObj = { monthlyQuota, monthlyTIC, cbmLinesSold, dataSalesSold, deals, nextDealId, monthLabel };
    localStorage.setItem(storageKey, JSON.stringify(stateObj));
    localStorage.setItem(historyKey, JSON.stringify(history));
    localStorage.setItem(pendingKey, JSON.stringify(pendingDI));
    syncToBackend(stateObj, history, pendingDI);
  }, [loaded, monthlyQuota, monthlyTIC, cbmLinesSold, dataSalesSold, deals, nextDealId, monthLabel, history, pendingDI, syncToBackend, storageKey, historyKey, pendingKey]);

  // --- Auto-rollover fiscal month (waits for backend data) ---
  useEffect(() => {
    if (!loaded) return;
    const currentFiscal = getFiscalMonthKey();
    if (monthLabel && monthLabel !== currentFiscal && monthLabel < currentFiscal) {
      const hasData = deals.length > 0 || (parseInt(cbmLinesSold) || 0) > 0 || (parseInt(dataSalesSold) || 0) > 0;
      if (hasData) {
        // Auto-archive the old month
        const cbmVal = parseInt(cbmLinesSold) || 0;
        const dataVal = parseInt(dataSalesSold) || 0;
        const q = parseFloat(monthlyQuota) || 0;
        const t = parseFloat(monthlyTIC) || 0;
        const tcr_ = q > 0 ? t / q : 0;
        const biMet = cbmVal >= CBM_TARGET && dataVal >= DATA_TARGET;
        const thresh = biMet ? 1.1 : 0.9;

        const getVas = (ct, vc) => {
          if (ct === 'new') return vc === 0 ? 0.9 : vc === 1 ? 1.1 : 1.2;
          return vc === 0 ? 0.9 : vc === 1 ? 1.0 : 1.2;
        };

        let nbiT = 0, renT = 0, netRC = 0;
        const diArchive = [];
        deals.forEach(d => {
          if (d.dealType === 'renewal') {
            const s = parseFloat(d.startingRevenue) || 0;
            const e = parseFloat(d.endingRevenue) || 0;
            const ret = s > 0 ? (e / s) * 100 : 0;
            renT += ret >= 100 ? 0.15 * Math.min(s, e) : 0;
            const inc = e > s ? e - s : 0;
            netRC += inc;
            nbiT += inc * tcr_ * thresh;
          } else {
            const rev = parseFloat(d.revenue) || 0;
            const vm = getVas(d.customerType, d.vasCount);
            const nbi = rev * vm * tcr_ * thresh;
            nbiT += nbi;
            netRC += rev * vm;
            if (d.isDI) {
              diArchive.push({
                id: Date.now() + Math.random(),
                name: d.name || 'Unnamed DI',
                connectHalf: nbi / 2,
                saleFiscalMonth: monthLabel,
                expectedConnectMonth: d.expectedConnectMonth || '',
              });
            }
          }
        });

        const achPct = q > 0 ? (netRC / q) * 100 : 0;
        let accBonus = 0;
        if (achPct >= 150) accBonus = 2200 + (achPct - 150) * 60;
        else if (achPct >= 125) accBonus = 950 + (achPct - 125) * 50;
        else if (achPct >= 110) accBonus = 350 + (achPct - 110) * 40;
        else if (achPct >= 100) accBonus = (achPct - 100) * 35;
        accBonus = Math.min(accBonus, 6000);

        setHistory(prev => {
          const snapshot = {
            month: monthLabel, netRevenueCredit: netRC, cbmLinesSold: cbmVal,
            dataSalesSold: dataVal, nbiTotal: nbiT, renewalsCommission: renT,
            accelerationBonus: accBonus, grandTotal: nbiT + renT + accBonus,
            achievementPct: achPct, dealCount: deals.length,
            quota: q, tic: t, deals: deals.map(d => ({ ...d })),
          };
          const filtered = prev.filter(h => h.month !== monthLabel);
          return [...filtered, snapshot].sort((a, b) => a.month.localeCompare(b.month)).slice(-12);
        });

        if (diArchive.length > 0) setPendingDI(prev => [...prev, ...diArchive]);
      }
      // Reset for new month
      setDeals([]);
      setCbmLinesSold('');
      setDataSalesSold('');
      setNextDealId(1);
      setMonthLabel(currentFiscal);
      setViewingMonth(currentFiscal);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded]);

  // Keep viewingMonth valid — prevents broken arrows when month isn't in available list
  useEffect(() => {
    const available = [...new Set([...history.map(h => h.month), monthLabel])].sort();
    if (!available.includes(viewingMonth)) {
      setViewingMonth(monthLabel);
    }
  }, [viewingMonth, monthLabel, history]);

  // --- Computed values ---
  const quota = parseFloat(monthlyQuota) || 0;
  const tic = parseFloat(monthlyTIC) || 0;
  const tcr = quota > 0 ? tic / quota : 0;
  const cbm = parseInt(cbmLinesSold) || 0;
  const dataCount = parseInt(dataSalesSold) || 0;
  const biCbmMet = cbm >= CBM_TARGET && dataCount >= DATA_TARGET;
  const thresholdMultiplier = biCbmMet ? 1.1 : 0.9;

  const getVasMultiplier = (customerType, vasCount) => {
    if (customerType === 'new') return vasCount === 0 ? 0.9 : vasCount === 1 ? 1.1 : 1.2;
    return vasCount === 0 ? 0.9 : vasCount === 1 ? 1.0 : 1.2;
  };

  const computeDealPayout = (deal) => {
    if (deal.dealType === 'renewal') {
      const start = parseFloat(deal.startingRevenue) || 0;
      const end = parseFloat(deal.endingRevenue) || 0;
      const retainedPct = start > 0 ? (end / start) * 100 : 0;
      const commission = retainedPct >= 100 ? 0.15 * Math.min(start, end) : 0;
      const incremental = end > start ? end - start : 0;
      const nbi = incremental * tcr * thresholdMultiplier;
      return { type: 'renewal', retainedPct, commission, incremental, nbi, revCredit: incremental, baseRev: incremental };
    }
    const rev = parseFloat(deal.revenue) || 0;
    const vasMultiplier = getVasMultiplier(deal.customerType, deal.vasCount);
    const nbi = rev * vasMultiplier * tcr * thresholdMultiplier;
    return { type: 'new_business', nbi, vasMultiplier, revCredit: rev * vasMultiplier, baseRev: rev };
  };

  const dealPayouts = deals.map(d => ({ deal: d, payout: computeDealPayout(d) }));
  const nbiTotal = dealPayouts.reduce((s, { payout: p }) => s + (p.nbi || 0), 0);
  const renewalsCommTotal = dealPayouts.reduce((s, { payout: p }) => s + (p.commission || 0), 0);
  const baseRevenue = dealPayouts.reduce((s, { payout: p }) => s + (p.baseRev || 0), 0);
  const netRevenueCredit = dealPayouts.reduce((s, { payout: p }) => s + (p.revCredit || 0), 0);
  const achievementPct = quota > 0 ? (netRevenueCredit / quota) * 100 : 0;

  const computeAccelerationBonus = (pct) => {
    if (pct < 100) return 0;
    let bonus;
    if (pct >= 150) bonus = 2200 + (pct - 150) * 60;
    else if (pct >= 125) bonus = 950 + (pct - 125) * 50;
    else if (pct >= 110) bonus = 350 + (pct - 110) * 40;
    else bonus = (pct - 100) * 35;
    return Math.min(bonus, 6000);
  };

  const accelerationBonus = computeAccelerationBonus(achievementPct);
  const grandTotal = nbiTotal + renewalsCommTotal + accelerationBonus;

  const currentDIDeals = dealPayouts
    .filter(({ deal }) => deal.isDI && deal.dealType === 'new_business')
    .map(({ deal, payout }) => ({
      name: deal.name || 'Unnamed DI',
      connectHalf: payout.nbi / 2,
      saleFiscalMonth: monthLabel,
      expectedConnectMonth: deal.expectedConnectMonth || '',
    }));

  const allPendingDI = [
    ...currentDIDeals.map((d, i) => ({ ...d, id: `current-${i}`, source: 'current' })),
    ...pendingDI.map(d => ({ ...d, source: 'archived' })),
  ];

  // --- Actions ---
  const addDeal = () => {
    const newId = nextDealId;
    setDeals([...deals, {
      id: newId, name: '', dealType: 'new_business',
      revenue: '', customerType: 'new', vasCount: 0, isDI: false,
      expectedConnectMonth: '', startingRevenue: '', endingRevenue: '', icomp: false,
    }]);
    setNextDealId(newId + 1);
    setEditingDealId(newId);
  };

  const updateDeal = (id, field, value) => {
    setDeals(deals.map(d => d.id === id ? { ...d, [field]: value } : d));
  };

  const removeDeal = (id) => {
    setDeals(deals.filter(d => d.id !== id));
    if (editingDealId === id) setEditingDealId(null);
  };

  const removePendingDI = (id) => {
    setPendingDI(pendingDI.filter(d => d.id !== id));
  };

  // Rolling averages
  const last3 = history.slice(-3);
  const avg = (arr, key) => arr.length > 0 ? arr.reduce((s, h) => s + (h[key] || 0), 0) / arr.length : 0;
  const rollingRevenue = avg(last3, 'netRevenueCredit');
  const rollingCBM = avg(last3, 'cbmLinesSold');
  const rollingData = avg(last3, 'dataSalesSold');

  const fiscalOptions = generateFiscalOptions(monthLabel, 8);

  // Month navigation
  const isCurrentMonth = viewingMonth === monthLabel;
  const viewedHistory = !isCurrentMonth ? history.find(h => h.month === viewingMonth) : null;
  const availableMonths = [...new Set([...history.map(h => h.month), monthLabel])].sort();
  const currentIdx = availableMonths.indexOf(viewingMonth);
  const hasPrev = currentIdx > 0;
  const hasNext = currentIdx < availableMonths.length - 1;
  const goPrev = () => { if (hasPrev) setViewingMonth(availableMonths[currentIdx - 1]); };
  const goNext = () => { if (hasNext) setViewingMonth(availableMonths[currentIdx + 1]); };

  const pastDealSummary = (deal) => {
    if (deal.dealType === 'renewal') {
      return `Renewal: ${fmt(parseFloat(deal.startingRevenue) || 0)} → ${fmt(parseFloat(deal.endingRevenue) || 0)}`;
    }
    const parts = [`Revenue ${fmt(parseFloat(deal.revenue) || 0)}`];
    if (deal.vasCount > 0) parts.push(`${deal.vasCount} VAS`);
    if (deal.isDI) parts.push('DI');
    return parts.join(' | ');
  };

  // Deal summary for compact list
  const getDealSummary = (deal, payout) => {
    if (deal.dealType === 'renewal') {
      const parts = [`${fmt(parseFloat(deal.startingRevenue) || 0)} → ${fmt(parseFloat(deal.endingRevenue) || 0)}`, `Commission: ${fmt(payout.commission)}`];
      if (payout.nbi > 0) parts.push(`NBI: ${fmt(payout.nbi)}`);
      return parts.join(' | ');
    }
    const parts = [`Revenue ${fmt(parseFloat(deal.revenue) || 0)}`, `${deal.vasCount} VAS`];
    if (deal.isDI) parts.push('DI');
    return `${parts.join(' | ')} → NBI: ${fmt(payout.nbi)}`;
  };

  if (!['acorvino', 'fgonzalez'].includes(user.username?.toLowerCase())) {
    return <div className="comp-calculator"><p>Access denied.</p></div>;
  }

  return (
    <div className="comp-calculator">
      <div className="comp-header">
        <button className="back-button" onClick={onBack}>&larr; Back</button>
        <h2>Comp Calculator</h2>
      </div>
      <div className="fiscal-month-bar">
        <button className="month-arrow" onClick={goPrev} disabled={!hasPrev}>&larr;</button>
        <span className="month-label-text">{getFiscalLabel(viewingMonth)}</span>
        <button className="month-arrow" onClick={goNext} disabled={!hasNext}>&rarr;</button>
      </div>
      {syncing && <div className="sync-indicator">Syncing...</div>}

      {isCurrentMonth ? (
        <>
          {/* Monthly Settings */}
          <section className="comp-section">
            <h3>Monthly Settings</h3>
            <div className="settings-grid">
              <label>
                Monthly Quota ($)
                <input type="number" value={monthlyQuota} onChange={(e) => setMonthlyQuota(e.target.value)} placeholder="e.g. 2800" />
              </label>
              <label>
                Monthly TIC ($)
                <input type="number" value={monthlyTIC} onChange={(e) => setMonthlyTIC(e.target.value)} placeholder="4166.67" />
              </label>
              <label>
                CBM Lines Sold
                <input type="number" value={cbmLinesSold} onChange={(e) => setCbmLinesSold(e.target.value)} placeholder="0" min="0" />
                <span className={`cbm-status ${cbm >= CBM_TARGET ? 'met' : 'not-met'}`}>
                  {cbm >= CBM_TARGET ? `Target met (${cbm})` : `${CBM_TARGET - cbm} more needed`}
                </span>
              </label>
              <label>
                Data Sales (Internet)
                <input type="number" value={dataSalesSold} onChange={(e) => setDataSalesSold(e.target.value)} placeholder="0" min="0" />
                <span className={`cbm-status ${dataCount >= DATA_TARGET ? 'met' : 'not-met'}`}>
                  {dataCount >= DATA_TARGET ? `Target met (${dataCount})` : `${DATA_TARGET - dataCount} more needed`}
                </span>
              </label>
            </div>
            <div className="settings-status-bar">
              <div className="tcr-display">
                <span className="tcr-label">TCR</span>
                <span className="tcr-value">{tcr > 0 ? tcr.toFixed(4) : '--'}</span>
              </div>
              <div className={`threshold-display ${biCbmMet ? 'met' : 'not-met'}`}>
                <span className="threshold-label">BI/CBM Threshold</span>
                <span className="threshold-value">
                  {biCbmMet ? 'Met' : 'Not Met'} ({cbm}/{CBM_TARGET} CBM, {dataCount}/{DATA_TARGET} Data) &rarr; {biCbmMet ? '110%' : '90%'}
                </span>
              </div>
            </div>
          </section>

          {/* Deals */}
          <section className="comp-section">
            <div className="section-header">
              <h3>Deals ({deals.length})</h3>
              <button className="add-button" onClick={addDeal}>+ Add Deal</button>
            </div>
            {deals.length === 0 && <p className="empty-message">No deals added yet.</p>}

            {dealPayouts.map(({ deal, payout }) => (
              editingDealId === deal.id ? (
                <div key={deal.id} className="deal-card">
                  <div className="deal-top-row">
                    <input type="text" className="deal-name-input" value={deal.name}
                      onChange={(e) => updateDeal(deal.id, 'name', e.target.value)}
                      placeholder="Deal / customer name" autoFocus />
                    <select className="deal-type-select" value={deal.dealType}
                      onChange={(e) => updateDeal(deal.id, 'dealType', e.target.value)}>
                      <option value="new_business">New Business</option>
                      <option value="renewal">Renewal</option>
                    </select>
                  </div>
                  {deal.dealType === 'new_business' ? (
                    <div className="deal-fields">
                      <label>Revenue ($)
                        <input type="number" value={deal.revenue} onChange={(e) => updateDeal(deal.id, 'revenue', e.target.value)} placeholder="0" />
                      </label>
                      <label>Customer
                        <select value={deal.customerType} onChange={(e) => updateDeal(deal.id, 'customerType', e.target.value)}>
                          <option value="new">New</option>
                          <option value="existing">Existing</option>
                        </select>
                      </label>
                      <label>VAS
                        <select value={deal.vasCount} onChange={(e) => updateDeal(deal.id, 'vasCount', parseInt(e.target.value))}>
                          <option value={0}>0</option>
                          <option value={1}>1</option>
                          <option value={2}>2+</option>
                        </select>
                      </label>
                      <label className="toggle-label">DI
                        <input type="checkbox" checked={deal.isDI} onChange={(e) => updateDeal(deal.id, 'isDI', e.target.checked)} />
                      </label>
                      {deal.isDI && (
                        <label>Expected Connect
                          <select value={deal.expectedConnectMonth} onChange={(e) => updateDeal(deal.id, 'expectedConnectMonth', e.target.value)}>
                            <option value="">TBD</option>
                            {fiscalOptions.map(k => <option key={k} value={k}>{getFiscalLabelShort(k)}</option>)}
                          </select>
                        </label>
                      )}
                    </div>
                  ) : (
                    <div className="deal-fields">
                      <label>Starting MRC ($)
                        <input type="number" value={deal.startingRevenue} onChange={(e) => updateDeal(deal.id, 'startingRevenue', e.target.value)} placeholder="0" />
                      </label>
                      <label>Ending MRC ($)
                        <input type="number" value={deal.endingRevenue} onChange={(e) => updateDeal(deal.id, 'endingRevenue', e.target.value)} placeholder="0" />
                      </label>
                    </div>
                  )}
                  <div className="deal-edit-actions">
                    <button className="done-button" onClick={() => setEditingDealId(null)}>Done</button>
                    <button className="remove-button" onClick={() => removeDeal(deal.id)}>Remove</button>
                  </div>
                </div>
              ) : (
                <div key={deal.id} className="deal-row" onClick={() => setEditingDealId(deal.id)}>
                  <span className="deal-row-name">{deal.name || 'Unnamed'}</span>
                  <span className="deal-row-summary">{getDealSummary(deal, payout)}</span>
                  <div className="deal-row-actions">
                    <label className="icomp-check" title="Check when this deal appears correctly on your comp statement" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={!!deal.icomp} onChange={(e) => updateDeal(deal.id, 'icomp', e.target.checked)} />
                      ICOMP
                    </label>
                    <button className="edit-button" onClick={(e) => { e.stopPropagation(); setEditingDealId(deal.id); }}>Edit</button>
                    <button className="remove-button" onClick={(e) => { e.stopPropagation(); removeDeal(deal.id); }}>Remove</button>
                  </div>
                </div>
              )
            ))}
          </section>

          {/* Results Summary */}
          <section className="comp-section results-section">
            <h3>Results Summary</h3>
            <div className="results-grid">
              <div className="result-row">
                <span>Base Revenue</span>
                <span className="result-value">{fmt(baseRevenue)}</span>
              </div>
              <div className="result-row">
                <span>Net Revenue Credit (w/ VAS)</span>
                <span className="result-value">{fmt(netRevenueCredit)}</span>
              </div>
              <div className="result-row">
                <span>NBI Total</span>
                <span className="result-value">{fmt(nbiTotal)}</span>
              </div>
              {renewalsCommTotal > 0 && (
                <div className="result-row">
                  <span>Renewal Commissions</span>
                  <span className="result-value">{fmt(renewalsCommTotal)}</span>
                </div>
              )}
              <div className="result-row">
                <span>Achievement</span>
                <span className="result-value">{achievementPct > 0 ? achievementPct.toFixed(1) + '%' : '--'}</span>
              </div>
              <div className="result-row">
                <span>Acceleration Bonus</span>
                <span className="result-value">{fmt(accelerationBonus)}</span>
              </div>
              <div className="result-row grand-total">
                <span>Grand Total</span>
                <span className="result-value">{fmt(grandTotal)}</span>
              </div>
              <div className="result-row payout-date-row">
                <span>Payout Date</span>
                <span className="result-value">{getPayoutDate(monthLabel)}</span>
              </div>
            </div>
          </section>
        </>
      ) : viewedHistory ? (
        <>
          {/* Past Month Summary */}
          <section className="comp-section results-section">
            <h3>Month Summary</h3>
            <div className="results-grid">
              {viewedHistory.quota != null && (
                <div className="result-row">
                  <span>Quota</span>
                  <span className="result-value">{fmt(viewedHistory.quota)}</span>
                </div>
              )}
              <div className="result-row">
                <span>Net Revenue Credit</span>
                <span className="result-value">{fmt(viewedHistory.netRevenueCredit)}</span>
              </div>
              <div className="result-row">
                <span>NBI Total</span>
                <span className="result-value">{fmt(viewedHistory.nbiTotal)}</span>
              </div>
              {(viewedHistory.renewalsCommission || 0) > 0 && (
                <div className="result-row">
                  <span>Renewal Commissions</span>
                  <span className="result-value">{fmt(viewedHistory.renewalsCommission)}</span>
                </div>
              )}
              <div className="result-row">
                <span>Achievement</span>
                <span className="result-value">{(viewedHistory.achievementPct || 0).toFixed(1)}%</span>
              </div>
              <div className="result-row">
                <span>Acceleration Bonus</span>
                <span className="result-value">{fmt(viewedHistory.accelerationBonus)}</span>
              </div>
              <div className="result-row">
                <span>CBM / Data</span>
                <span className="result-value">{viewedHistory.cbmLinesSold || 0} CBM, {viewedHistory.dataSalesSold || 0} Data</span>
              </div>
              <div className="result-row grand-total">
                <span>Grand Total</span>
                <span className="result-value">{fmt(viewedHistory.grandTotal)}</span>
              </div>
              <div className="result-row payout-date-row">
                <span>Payout Date</span>
                <span className="result-value">{getPayoutDate(viewingMonth)}</span>
              </div>
            </div>
          </section>

          {/* Past Month Deals */}
          {viewedHistory.deals?.length > 0 && (
            <section className="comp-section">
              <h3>Deals ({viewedHistory.deals.length})</h3>
              {viewedHistory.deals.map((d, i) => (
                <div key={i} className="deal-row past-deal">
                  <span className="deal-row-name">{d.name || 'Unnamed'}</span>
                  <span className="deal-row-summary">{pastDealSummary(d)}</span>
                </div>
              ))}
            </section>
          )}
        </>
      ) : (
        <section className="comp-section">
          <p className="empty-message">No data for this month.</p>
        </section>
      )}

      {/* Pending DI Payouts */}
      {allPendingDI.length > 0 && (
        <section className="comp-section di-section">
          <h3>Pending DI Payouts</h3>
          <div className="di-table-wrapper">
            <table className="history-table">
              <thead>
                <tr><th>Deal</th><th>Connect Half</th><th>Sale Payout</th><th>Connect Payout</th><th></th></tr>
              </thead>
              <tbody>
                {allPendingDI.map((d) => (
                  <tr key={d.id}>
                    <td style={{ fontFamily: 'inherit' }}>{d.name}</td>
                    <td>{fmt(d.connectHalf)}</td>
                    <td>{getPayoutDate(d.saleFiscalMonth)}</td>
                    <td>{d.expectedConnectMonth ? getPayoutDate(d.expectedConnectMonth) : 'TBD'}</td>
                    <td>
                      {d.source === 'archived' && (
                        <button className="remove-button" onClick={() => removePendingDI(d.id)}>Remove</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* 90-Day Rolling Averages */}
      <section className="comp-section rolling-section">
        <h3>90-Day Rolling Averages</h3>
        {last3.length === 0 ? (
          <p className="empty-message">No monthly history yet. Complete a month to start tracking.</p>
        ) : (
          <div className="rolling-grid">
            <div className="rolling-card">
              <span className="rolling-label">Avg Net Rev Credit</span>
              <span className="rolling-value">{fmt(rollingRevenue)}</span>
              <span className="rolling-detail">Based on {last3.length} month{last3.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="rolling-card">
              <span className="rolling-label">Avg CBM</span>
              <span className={`rolling-value ${rollingCBM >= CBM_TARGET ? 'cbm-good' : 'cbm-bad'}`}>{rollingCBM.toFixed(1)}</span>
              <span className="rolling-detail">Target: {CBM_TARGET}/month</span>
            </div>
            <div className="rolling-card">
              <span className="rolling-label">Avg Data</span>
              <span className={`rolling-value ${rollingData >= DATA_TARGET ? 'cbm-good' : 'cbm-bad'}`}>{rollingData.toFixed(1)}</span>
              <span className="rolling-detail">Target: {DATA_TARGET}/month</span>
            </div>
          </div>
        )}
      </section>

      {/* Monthly History */}
      <section className="comp-section history-section">
        <h3>Monthly History (Last 12 Months)</h3>
        {history.length === 0 ? (
          <p className="empty-message">No history yet.</p>
        ) : (
          <div className="history-table-wrapper">
            <table className="history-table">
              <thead>
                <tr><th>Month</th><th>Net Rev</th><th>CBM</th><th>Data</th><th>Deals</th><th>Ach %</th><th>Payout</th></tr>
              </thead>
              <tbody>
                {[...history].reverse().map((h) => (
                  <tr key={h.month} className={h.month === viewingMonth ? 'history-active' : ''} onClick={() => setViewingMonth(h.month)} style={{ cursor: 'pointer' }}>
                    <td>{getFiscalLabelShort(h.month)}</td>
                    <td>{fmt(h.netRevenueCredit)}</td>
                    <td className={h.cbmLinesSold >= CBM_TARGET ? 'cbm-good' : 'cbm-bad'}>{h.cbmLinesSold}</td>
                    <td className={(h.dataSalesSold || 0) >= DATA_TARGET ? 'cbm-good' : 'cbm-bad'}>{h.dataSalesSold || 0}</td>
                    <td>{h.dealCount}</td>
                    <td>{(h.achievementPct || 0).toFixed(1)}%</td>
                    <td>{fmt(h.grandTotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default CompCalculator;
